
import { CVE_PORTAL } from "src/app/constants/cve_portal";
import * as uuid from 'uuid';
import { LETTERS, NUMBERS_ROMANIZW, SIMBOL_VINIETA_SPACE } from "../constants/formatList";
import { TranslateService } from "@ngx-translate/core";
import { IMention } from "src/app/components/text-editor/service/text-editor.service";
import { IValidationImgSize } from "src/app/model/utils/validation-sizes";
import { ITree } from "src/app/model/tree-custom/tree.interface";
import { getHours, getMinutes, getSeconds, isValid, parseISO, setHours, setMinutes, setSeconds } from 'date-fns';
import { REGEX_PATTERNS } from "../constants/regex-patterns";

export const UTILS = {
  /**
   * Obtiene la propiedad de un objeto, si no existe por default regresa false o el valor default
   * @param fn callback con la propiedad a extraer () => obj.property
   * @param valueDefault valor por default en caso de no existir la propiedad
   * @returns La propiedad buscada o el valor default en caso de ser undefined.
   */
  getPropertyValue(fn: Function, valueDefault?: any) {
    try {
      let value = fn();
      if (value === undefined) {
        return valueDefault ?? false;
      }
      return fn();
    } catch {
      return valueDefault ?? false;
    }
  },

  /**
   * Obtiene una propiedad si no existe retorna el valor por default
   * @param fn callback con la propiedad a extraer () => obj.property
   * @param valueDefault valor por default en caso de no existir la propiedad
   * @returns La propiedad buscada o el valor default en caso de ser undefined
   */
  getProperty(fn: Function, valueDefault: any) {
    try {
      let value = fn();
      return value === undefined
        ? valueDefault
        : fn();
    } catch {
      return valueDefault;
    }
  },

  /**
   * Retornar un numero en formato moneda $X.00
   * @param amount Monto a parsear
   * @returns Formato moneda: 5 = $5.00
   */
  formatCurrency(amount: number) {
    let formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    });
    return formatter.format(amount);
  },



  /**
  * Retorna una validación AND de un conjunto de validaciones del mismo tipo, NO OBJETOS
  * @param validations Arreglo con validaciones del mismo tipo Ex: [ isActivated, cad === 'cadena', x > 2 ] o [ 'a' ,'b]
  * @param flag Bandera para saber si todas las validaciones cumplen False o True
  * @returns true | false
  */
  getAndValidation(validations: any[], flag: any) {
    return validations.every((validation) => validation === flag);
  },



  /**
   * Retorna una validación OR de un conjunto de validaciones del mismo tipo, NO OBJETOS
   * @param validations Arreglo con validaciones del mismo tipo Ex: [ isActivated, cad === 'cadena', x > 2 ] o [ 'a' ,'b]
   * @param flag Bandera para saber si alguna de las validaciones cumplen con el criterio
   * @returns true | false
   */
  getOrValidation(validations: any[], flag: any) {
    return validations.includes(flag);
  },

  /**
   * Validar si 2 strings son iguales, ignorando Upper y Lower
   * @param stg cadena 1
   * @param stg1 cadena 2
   * @returns true | false
   */
  isEqualString(stg: string, stg1: string) {
    return stg.trim().toLowerCase() === stg1.trim().toLowerCase();
  },


  /**
   * Verifica que un objeto no contenga propiedades undefined
   * @param o objeto
   */
  required(o: any) {
    for (const key of Object.keys(o)) {
      if (o[key] === null || o[key] === undefined) {
        throw new Error(`El parametro ${key} es requerido`);
      }
    }
  },

  /**
   * Convierte un numero a numero con n decimales
   * @param x Numero
   * @param decimals: Cantidad de decimales requeridos
   * @returns Ex 2.12345 a 2 decimales => 2.12
   */
  toDecimals(x: number, decimals: number) {
    return +x.toFixed(decimals);
  },

  /**
   * Convierte el nombre de usuario a formato 1er nombre y apellido paterno
   * @param completeName Nombre completo
   * @returns Diego A. Cárcamo Ortega => Diego Cárcamo
   */
  formatNameUser(completeName: string) {
    let catalog: any = {
      "2": 1,
      "3": 2,
      "4": 2
    }
    let arraySpace = completeName.split(' ');

    return `${arraySpace[0]} ${arraySpace[catalog[arraySpace.length]] || ''}`;
  },

  /**
  * Convierte el nombre corto a sus iniciales
  * @param shortName Diego Cárcamo
  * @returns Diego Cárcamo => DC
  */
  formatLettersNameUser(shortName: string) {
    let arraySpace = shortName.split(' ');
    let firtsLetter = arraySpace[0].substring(0, 1).toUpperCase();
    let secondLetter = arraySpace[1]?.substring(0, 1).toUpperCase() || '';

    return `${firtsLetter}${secondLetter}`;
  },
  /**
   * Obtiene el nodo de un array de arboles
   * @param key Propiedad del arbol en el que se va buscar
   * @param nodes Array de arboles
   * @param value Valor a buscar
   * @returns Nodo con todas sus propiedades
   */
  searchNodeByKey(key: string, nodes: any[], value: any): any {

    let nodeData = undefined;

    for (const node of nodes) {

      if (node[key] === value) {
        return node;
      }

      if (node.children && node.children.length > 0) {
        nodeData = this.searchNodeByKey(key, node.children, value);
        if (nodeData !== undefined) {
          return nodeData;
        }
      }

    }

    return nodeData;

  },

  /**
  * Obtiene el nodo de un array de arboles, siempre y cuando sean del mismo nivel
  * @param key Propiedad del arbol en el que se va buscar
  * @param nodes Array de arboles
  * @param value Valor a buscar
  * @param levelToSearch Nivel del nodo
  * @param currentLevel Nivel actual del nodo ( es un incrementador por default )
  * @returns Nodo con todas sus propiedades
  */
  searchNodeByKeyWithLevel(key: string, nodes: any[], value: any, levelToSearch: number, currentLevel: number = 0): any {

    let nodeData = undefined;

    for (const node of nodes) {

      if (`${node[key]}`.toLowerCase().trim() === `${value}`.toLowerCase().trim() && levelToSearch === currentLevel) {
        return node;
      }

      if (node.children && node.children.length > 0) {
        nodeData = this.searchNodeByKeyWithLevel(key, node.children, value, levelToSearch, currentLevel + 1);
        if (nodeData !== undefined) {
          return nodeData;
        }
      }

    }

    return nodeData;

  },

  /**
   * ejecuta un callback si encuentra el valor en el array
   * @param arr any[]
   * @param property string
   * @param value any
   * @param fn function
   */
  existInArrayAndRunFunction(arr: any[], property: string, value: any, fn: Function) {
    const index = arr.findIndex((option: any) => option[property] === value);
    if (index >= 0) {
      fn(index)
    }
  },

  /**
     * Retorna el elemento encontrado en un array de objetos
     * @param arr any[]
     * @param property string
     * @param calue any
     * @returns elemento encontrado o false si no se encontro ningun elemento
     */
  findInArray(arr: any[], property: string, value: any) {

    if (!Array.isArray(arr)) {
      return false
    }

    return arr.find((option: any) => option[property] === value) || false;
  },


  /**
   * Retorna un array filtrado
   * @param arr any[]
   * @param property string
   * @param calue any
   * @returns array filtrado
   */
  filterArrayOfObj(arr: any[], property: string, value: any) {
    return arr.filter((child: any) => child.hasOwnProperty(property) && child[property] === value);
  },

  /**
   * Prende aquellos nodos que se que contienen el criterio y los que no se apagan
   * IMPORTANTE: Los nodos deben de tener estas 2 propiedades
   * - isActiveInFilter: boolean = Bandera para prender o apagar el odo
   * - filteredChildren:  nodo[] = hijos con isActiveInFilter prendido
   * @param key propiedad a usar de los nodos
   * @param texto valor a buscar
   * @param tree array de arboles o nodos
   * @param fatherActive si el padre contiene el criterio, va dejar activos a los hijos (este parámetro solo se usa dentro el método al ser recursivo)
   * @returns
   * - Árbol con nodos prendidos y apagados
   * - Dentro de cada nodo, contendrá los hijos que estén prendidos o apagados
   *
   */
  filterTree(key: string, texto: string, tree: any[], fatherActive?: boolean): any {

    texto = texto.trim();

    for (const nodo of tree) {

      const value = nodo[key];
      const nodeIncludesText = `${value}`.toLowerCase().includes(texto.toLowerCase());

      nodo.isActiveInFilter = fatherActive !== undefined && fatherActive
        ? true
        : nodeIncludesText;

      if (nodo.children.length > 0) {

        nodo.filteredChildren = this.filterTree(key, texto, nodo.children, nodo.isActiveInFilter);

        if (texto === "") {
          nodo.filteredChildren = [];
        }

        nodo.isActiveInFilter = nodeIncludesText || nodo.children.some((child: any) => child.isActiveInFilter);
      }

    }

    return tree.filter((node) => node.isActiveInFilter);

  },

  /**
   * Retorna un objeto clon o array de objetos clon, sin referencia
   * @param object Objeto
   * @returns Clon del objeto sin referencia
   */
  clonObject<T>(object: T): T {
    return JSON.parse(JSON.stringify(object)); // NOSONAR
  },

  /**
   * Lanza un error con un mensaje
   * @param msg Mensaje de error
   */
  throwError(msg: string) {
    throw new Error(msg);
  },


  /**
   * Separa un array de objetos por una propiedad comun, por ejemplo de un array de usuarios juntarlos por edad
   * @param objects Array de objetos
   * @param key
   * @returns
   */
  joinArrayByProperty(objects: any[], key: string) {

    const joinArray: { property: any, items: any[] }[] = []

    for (const object of objects) {

      const objectByProperty = joinArray.find((ele) => ele.property === object[key]);

      (objectByProperty)
        ? objectByProperty.items.push(object)
        : joinArray.push({ property: object[key], items: [object] });

    }

    return joinArray;
  },

  /**
   * Regresa los filtros para consulta portal admin (PDA) o alumnos (PAW y PAM)
   * @param cvePortal clave del portal
   */
  returnParamsCvePortal(cvePortal: string) {
    let filtersCve = '';

    if (cvePortal === CVE_PORTAL.Admin) {
      filtersCve += `&site=${CVE_PORTAL.Admin}`;
    } else {
      filtersCve += `&site=${CVE_PORTAL.Web}&site=${CVE_PORTAL.Mobile}`;
    }
    return filtersCve;
  },

  /**
   * Verifica si una fecha es igual a la de hoy
   * @param date fecha input
   * @returns true si la fecha introducida es igual a la de hoy
   */
  isDateToday(date: string) {

    const otherDate = new Date(date);
    const todayDate = new Date();

    const validations = [
      otherDate.getDate() === todayDate.getDate(),
      otherDate.getMonth() === todayDate.getMonth(),
      otherDate.getFullYear() === todayDate.getFullYear()
    ];

    return this.getAndValidation(validations, true);

  },

  /**
   * Convierte una fecha a tiempo UTC
   * @param date
   * @returns fecha en tiempo UTC
   */
  dateToUTC(date: Date) {
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), date.getUTCHours(), date.getUTCMinutes(), date.getUTCSeconds()));
  },

  /**
   * Convierte una fecha UTC al tiempo actual del usuario
   * @param date fecha en tiempo UTC
   * @returns fecha en el timezone del usuario
   */
  utcToDate(date: string | Date) {

    const utcDate = new Date(date);

    // Get the difference between UTC time and local time in minutes
    const timezoneOffset = utcDate.getTimezoneOffset();

    // Convert the UTC time to local time by subtracting the timezone offset
    return new Date(utcDate.getTime() - timezoneOffset * 60 * 1000);
  },


  /**
   * Regresa los header en formato para filtro por tabla de check y multiselect
   * @param value
   * @param id
   */
  buildOptionSelectHeader(value: string, id: number | string) {
    return {
      id,
      value,
      uid: uuid.v4(),
      isActive: false,
      isActiveInFilter: false,
      filteredChildren: [],
      children: []
    }
  },


  /**
   * Regresa el numero de meses entre una fecha y otra
   * @param date1 fecha inicial
   * @param date2 fecha final
   */
  monthsBetweenDates(date1: Date, date2: Date) {
    let months;
    let years = date2.getFullYear() - date1.getFullYear()

    months = (years) * 12;
    months -= date1.getMonth();
    months += date2.getMonth();

    return Math.max(0, months);

  },

  /**
   * Ordena un array por campo por referencia
   * @param array: Array
   * @param field: Campo a ordenar
   * @param isAsc: Ordenar por ascendente
   * @return Ordena el arreglo por referencia
   */
  sortArray(array: any, field: string, isAsc: boolean) {

    if (!Array.isArray(array)) {
      return;
    }

    array.sort((a: any, b: any) => {

      const valueMajor = isAsc ? 1 : -1;
      const valueMinor = isAsc ? -1 : 1;

      if (a[field] > b[field]) {
        return valueMajor;
      }

      return a[field] < b[field] ? valueMinor : 0;

    });


  },
  /**
   * Retona el tipo al que pertenece el dato
   * @param data Dato
   * @returns tipo de dato Ej: [1,1] = 'array' o new Date() = 'date'
   */
  getType(data: any): 'array' | 'date' | 'object' | 'undefined' | 'boolean' | 'number' | 'string' | 'bigint' | 'symbol' | 'function' {

    if (Array.isArray(data)) {
      return 'array';
    }

    return (data instanceof Date)
      ? 'date'
      : typeof (data);

  },
  toValidDate(input: Date | string): Date {
    const date = typeof input === 'string' ? parseISO(input) : input;
    if (!isValid(date)) {
      throw new Error(`Invalid date: ${input}`);
    }
    return date;
  },
  /**
   * Une un tiempo con una fecha especifica
   * @param date Fecha que cambiara su hora
   * @param time Fecha que se le extraerá el tiempo
   * @returns Fecha combinada o ajustada
   */
  joinDateAndTime(dateInput: Date, timeInput: Date): Date {

    const date = this.toValidDate(dateInput);
    const time = this.toValidDate(timeInput);

    const hours = getHours(time);
    const minutes = getMinutes(time);
    const seconds = getSeconds(time);

    let combined = setHours(date, hours);
    combined = setMinutes(combined, minutes);
    combined = setSeconds(combined, seconds);

    return combined;
  },
  /**
   * Retorna si dos fechas son iguales ignorando las horas
   * @returns
   */
  isDatesEquals(start: Date, end: Date) {
    const newStart = start.setHours(0, 0, 0, 0);
    const newEnd = end.setHours(0, 0, 0, 0);

    return newStart === newEnd;
  },


  /**
    * Retorna si dos fechas son iguales ignorando las horas
    * @returns
    */
  changeCaracteresSpecials(value: string, matchReGex: string, caracter: string = "_") {
    const regex = new RegExp(matchReGex);

    return value
      .split("")
      .map(letter => (regex.exec(letter) ? letter : caracter))
      .join("");
  },

  /**
     * limpia el string de los metadatos raros que paresen signo de interrogacion
     * @param value string with medatada <?>
     * @returns
     */
  remplateStrings(value: string = "") {
    return value?.replaceAll(/[\u003F\u200B\uFEFF]/g, '') || "";
  },

  /**
    * tranforma un numero a numero romano
    * @param value number
    * @returns numero romano
    */
  romanize(num) {
    let lookup = NUMBERS_ROMANIZW
    let roman = '';
    let i;
    for (i in lookup) {
      while (num >= lookup[i]) {
        roman += i;
        num -= lookup[i];
      }
    }
    return roman;
  },
  /**
   * setea el tipo de lista que se va a mostrar
   * @param value number
   * @param type string
   * @returns simbol
   */
  setValueList(value, type) {
    let valueSimbol;
    if (type === "romanize") {
      valueSimbol = this.romanize(value)
    } else if (type === "letter") {
      valueSimbol = LETTERS[value - 1]
    } else if (type === "number") {
      valueSimbol = value
    } else if (type === "vi") {
      valueSimbol = SIMBOL_VINIETA_SPACE
    }
    return valueSimbol
  },
  /**
   * Valida si un string incluye un substring ignorando mayusculas y minusculas
   * @param value Cadena completa
   * @param search Substring
   * @returns Valor boolean si incluye
   */
  includesStringIgnoreCase(value: string, search: string) {
    return value.trim().toLowerCase().includes(search.trim().toLowerCase())
  },
  /**
   * Remueve un nodo de un arbol por su id
   * @param key Clave del nodo
   * @param tree Arbol
   * @param id Id del nodo
   * @returns Arbol sin el nodo
   */
  removeNodeById(key: string, tree: any[], id: any) {

    // Si la lista de nodos está vacía o no está definida, devuelve una lista vacía
    if (!Array.isArray(tree)) return [];

    return tree.reduce((acc, node) => {

      if (`${node[key]}` !== `${id}`) {
        acc.push({
          ...node,
          children: this.removeNodeById(key, node.children || [], id)
        });
      }

      return acc;

    }, []);

  },
  /**
  * Crear la ruta de la pagina
  * @param translate_instance .translateService.instan instancia del trasnlate
  * @param title todos los titulos seprados por comas
  */
  getRoutePage(translate: TranslateService, ...title: string[]) {
    return title
      .map((t, idx) =>
        title.length - 1 === idx
          ? `<b>${translate.instant(t)}</b>`
          : translate.instant(t)
      )
      .join(' / ');
  },
  /**
   * Crea las menciones para los text-editor
   * @param params lista de parametros
   * @param id nombre de la propiedad para el id
   * @param value nombre de la propiedad para el value
   * @param isSimpleParams es parametro simple
   */
  getMentions<T>(
    params: T[],
    id: keyof T,
    value: keyof T,
    isSimpleParams: boolean = true
  ): IMention[] {
    return params.map(
      (p) =>
      ({
        id: p[id],
        value: p[value],
        isSimpleParams,
      } as IMention)
    );
  },
  /**
   * Transforma un objeto en uno apto para los select
   * @param params lista de parametros
   * @param id nombre de la propiedad para el id
   * @param name nombre de la propiedad para el name
   */
  getSelectValues<T>(
    params: T[],
    id: keyof T,
    name: keyof T,
  ): { id: string | number, name: string }[] {
    return params.map(
      (p) =>
      ({
        id: p[id],
        name: p[name],
      } as { id: string | number, name: string })
    );
  },
  /**
   * Obtiene la propiedad de un objeto a partir del path en el objeto, si no existe regresa undefined
   * @param object Objeto
   * @param path Path de la proiedad Ex: attribute.subAttribute
   * @returns El valor o undefined
   */
  getPropertyByPath(object: any, path: string, defaultValue?: any) {
    let result = object;

    for (const part of path.split('.')) {
      result = result?.[part];
      if (result === undefined) {
        return defaultValue;
      }
    }

    return result;
  },

  /**
   * Asigna una propiedad a un objeto a partir de un path, si no existe lo crea
   * @param object Objecto
   * @param path  Path de la proiedad Ex: attribute.subAttribute
   * @param value Valor a asignar
   */
  setPropertyByPath(object: any, path: string, value: any) {

    const keys = path.split('.');
    const lastKey: any = keys.pop();

    const target = keys.reduce((acc, part) => {
      if (!acc[part]) {
        acc[part] = {}; // Crear un objeto si no existe
      }
      return acc[part];
    }, object);

    target[lastKey] = value;

  },
  /**
   * Retonar la intersección entre 2 arrays por una propiedad
   * @param array1 Conjunto 1
   * @param array2 Conjunto 2
   * @param property Propiedad a comparar
   * @returns Array de elementos en la intersección
   */
  getInsersectionByValue(array1: any[], array2: any[], property: string) {

    const intersectionElements: any[] = [];

    for (const element of array1) {

      const isPresent = UTILS.findInArray(array2, property, element[property]);

      isPresent && intersectionElements.push(element);

    }

    return intersectionElements;

  },
  /**
   * Valida que las dimensiones de una imagen sean correctas
   * @param file Archivo de imagen
   * @param validationSizes Dimensiones de validación
   * @returns True si las dimensiones son correctas, false en caso contrario
   */
  async validateImageDimensions(file: File, validationSizes: IValidationImgSize): Promise<boolean> {
    try {
      const img = await this.loadImage(file);
      return this.checkImageDimensions(img, validationSizes);
    } catch {
      return false;
    }
  },
  loadImage(file: File): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e: ProgressEvent<FileReader>) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('Error al cargar la imagen.'));
        img.src = e.target?.result as string;
      };

      reader.onerror = () => reject(new Error('Error al cargar la imagen.'));
      reader.readAsDataURL(file);
    });
  },
  checkImageDimensions(img: HTMLImageElement, validationSizes: IValidationImgSize): boolean {
    const { width, height } = img;
    const { aspectRatio, maxDimensions } = validationSizes;

    const ratio = width / height;
    const desiredRatio = aspectRatio.width / aspectRatio.height;
    const tolerance = 0.01; // Tolerancia para pequeñas diferencias

    // Verifica la relación de aspecto y las dimensiones máximas
    const isAspectRatioValid = Math.abs(ratio - desiredRatio) < tolerance;
    const isWithinMaxDimensions = width <= maxDimensions.width && height <= maxDimensions.height;

    return isAspectRatioValid || isWithinMaxDimensions;
  },

  /**
   * Ordena y limpia por activos "status"
   * @param array
   * @param key
   * @param onlyActive
   * @returns
   */
  orderAndCleanRegisters(array: any[], key: string, onlyActive: boolean = false) {

    const filteredArray = onlyActive
      ? array.filter((item) => item.status)
      : array

    this.sortArray(filteredArray, key, true);

    return filteredArray;

  },
  /**
   * Obtiene los nodos hoja de un nivel del arbol, excluyendo nodos que tengan hijos o que su hijo tenga la misma configuracion
   * @param tree
   * @returns Nodos hoja
   */
  getLeafNodesOneLevel(tree: ITree): ITree[] {

    const { children } = tree;

    return children.filter(child => child?.title === child.children[0]?.title || child.children.length === 0);

  },
  /**
   * Obtiene los nodos hijos
   * @param tree
   * @returns Nodos hijos
   */
  getLeafNodes(tree: ITree[]): ITree[] {
    const leaves: ITree[] = [];

    const traverse = (node: ITree) => {
      if (!node.children || node.children.length === 0) {
        leaves.push(node);
        return;
      }

      for (const child of node.children) {
        traverse(child);
      }
    };

    for (const node of tree) {
      traverse(node);
    }

    return leaves;
  },

  /**
   * Encuentra la raiz al que pertenece un nodo
   * @param tree
   * @param uuid
   * @returns Nodo raiz
   */
  findRootByLeafUUID(tree: ITree[], uuid: string): ITree | null {

    function findPath(node: ITree, path: ITree[]): ITree[] | null {
      path.push(node); // Agregar nodo actual al camino

      if (node.uuid === uuid) {
        return path; // Se encontró el nodo, devolver el camino completo
      }

      for (const child of node.children || []) {
        const result = findPath(child, [...path]); // Pasar una copia del camino
        if (result) return result;
      }

      return null; // No se encontró el nodo en este subárbol
    }

    for (const root of tree) {
      const path = findPath(root, []);
      if (path) return path[0]; // El primer elemento del camino es la raíz
    }

    return null; // No se encontró el nodo
  },
  /**
   * Divide un nombre completo en nombres y apellidos
   * @param fullName Nombre completo
   * @returns Objeto con nombres y apellidos
   */
  splitNames(fullName: string) {

    const specialTokens = new Set(['de', 'del', 'de la', 'van', 'von', 'da', 'dos', 'das']);

    const nameParts = fullName.trim().split(/\s+/);

    if (nameParts.length < 2) {
      return {
        names: nameParts[0] || '',
        lastName: '',
        motherLastName: '',
        error: 'Nombre muy corto'
      };
    }

    // Encontrar donde terminan los nombres y empiezan los apellidos
    let indexLastNames = Math.max(1, nameParts.length - 2);

    // Ajustar si hay partículas
    for (let i = 1; i < nameParts.length - 1; i++) {
      if (specialTokens.has(nameParts[i].toLowerCase())) {
        indexLastNames = i;
        break;
      }
    }

    const names = nameParts.slice(0, indexLastNames).join(' ');
    const lastNames = nameParts.slice(indexLastNames);

    return {
      names: names,
      lastName: lastNames[0] || '',
      motherLastName: lastNames.slice(1).join(' ') || '',
      error: null
    };

  },
  /**
 * Renombra un archivo
 * @param file Instancia de archivo
 * @param name Nuevo nombre con extension
 * @returns Archivo renombrado
 */
  renameFile(file: File, name: string) {

    const lastIndexPoint = name.lastIndexOf(".");

    const nameFile = name.substring(0, lastIndexPoint);

    const extention = name.substring(lastIndexPoint + 1);

    const cleanedName = this.changeCaracteresSpecials(nameFile, REGEX_PATTERNS.NAME_NOT_SPECIALS_CARACTERS);

    return new File([file], `${cleanedName}.${extention}`, {
      type: file.type
    });
  },
  /**
   * Limpia un texto de caracteres especiales
   * @param text Texto a limpiar
   * @returns Texto limpio
   */
  cleanTextOnlyLetters(text: string) {
    return text.replaceAll(/[^a-zA-ZáéíóúÁÉÍÓÚüÜñÑ\s]/g, '');
  },
  /**
   * Convierte un mapa a un array
   * @param map Mapa a convertir
   * @returns Array con los valores del mapa
   */
  mapToArray(map: Map<any, any>) {

    return Array.from(map.values());

  },
  /**
   * Convierte una cadena base64 a un objeto File
   * @param base64 Cadena base64
   * @param filename Nombre del archivo
   * @param mimeType Tipo MIME del archivo
   * @param notPrefix Indica si el base64 tiene el prefijo data:image/png;base64,
   * @returns Objeto File
   */
  base64ToFile(base64, filename, mimeType, notPrefix = false) {

    const byteString = notPrefix ? atob(base64) : atob(base64.split(',')[1]);

    const ab = new ArrayBuffer(byteString.length);

    const ia = new Uint8Array(ab);

    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.codePointAt(i)!;
    }

    return new File([ab], filename, { type: mimeType });

  },
  /**
   * Verifica si dos objetos son iguales en las propiedades especificadas
   * @param obj1 Primer objeto
   * @param obj2 Segundo objeto
   * @param keys Array de propiedades a comparar
   * @returns True si los objetos son iguales en las propiedades especificadas
   */
  areEqualObjectByProp(obj1: any, obj2: any, keys: string[]) {

    for (const prop of keys) {

      if (obj1[prop] !== obj2[prop]) {
        return false;
      }
    }

    return true;

  },
  /**
   * Convierte una cadena de fecha y hora a un objeto Date sin usar UTC
   * @param dateStringWithTime Cadena de fecha y hora en formato YYYY-MM-DDTHH:mm
   * @returns Objeto Date sin convertir a UTC
   */
  stringToDateOnly(dateStringWithTime: string): Date | null {

    if (!dateStringWithTime) {
      return null;
    }

    const [dateString] = dateStringWithTime.split('T');
    const [year, month, day] = dateString.split('-').map(Number);

    return new Date(year, month - 1, day);
  },
  /**
   * Convierte una fecha en formato string o Date a un objeto Date
   * @param date Fecha en formato string o Date
   * @returns Objeto Date
   */
  returnDateAsUTC(date: string | Date) {

    if (typeof date === 'string') {
      return date;
    }

    return date.toISOString();

  },
  /**
 * Obtiene la propiedad de un objeto, si no existe por default regresa false o el valor default proporcionado.
 * @param property Valor a extraer prop.name
 * @param defaultValue valor por default en caso de no existir la propiedad
 * @returns La propiedad buscada o el valor default en caso de ser undefined
 */
  getPropertyWithoutArrowNullish(property: any, defaultValue?: any) {
    return property || defaultValue;
  },

  /**
   * Obtiene la propiedad sin arrow function, con fallback solo si es null/undefined.
   * @param property valor directo a evaluar
   * @param defaultValue valor por default si property es null o undefined
   * @returns property si existe, defaultValue en caso contrario
   */
  getPropertyWithoutArrow(property: any, defaultValue?: any) {
    return property ?? defaultValue;
  }

}

